"use client";

import { useMediaQuery } from "@/app/directories/companion/(main)/roster/hooks/useMediaQuery";
import { AccountModalSuperscriptIcon } from "@frontend/components/account-modal/AccountModalSuperscriptIcon";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@frontend/shared/components/ui/popover";
import { cn } from "@frontend/shared/lib/utils";
import { motion } from "framer-motion";
import { Dot, HeartHandshakeIcon } from "lucide-react";
import { useState } from "react";
import type { KioskMachineContext } from "../../machine/types";
import { useKiosk } from "../kiosk-provider";

interface KioskHeaderProps {
  machineContext: KioskMachineContext & Record<string, unknown>;
  currentStateName: string;
  logoAlt?: string;
}

export function KioskHeader({
  machineContext,
  currentStateName,
  logoAlt = "Logo",
}: KioskHeaderProps) {
  const { insurances, firstName, lastName } = machineContext;
  const primaryInsurance = insurances?.primaryInsurance || null;
  const secondaryInsurance = insurances?.secondaryInsurance || null;
  const [open, setOpen] = useState(false);
  const pendingPricesMode = machineContext.pendingPricesMode;
  const { bundle, isLoading, error, machine, state, send } = useKiosk();
  const t = useTranslations("Kiosk.MedriteCheckin");
  const HEADER_VISIBLE_STATES = [
    "storefrontBrowse",
    "specificTreatment",
    "defaultTreatment",
  ] as const;

  const showHeader = HEADER_VISIBLE_STATES.includes(
    currentStateName as (typeof HEADER_VISIBLE_STATES)[number],
  );

  const displayProfileModal = true;

  const handleAddInsurance = () => {
    send({
      type: "ADD_INSURANCE",
      data: { addingInsuranceFromStorefront: true },
    });
  };

  const pricingStatus = "SUCCESS"; // Placeholder, replace with actual status
  const hasInsurance = primaryInsurance || secondaryInsurance;

  // media query for md and up
  const isMobile = useMediaQuery("(max-width: 768px)");
  return (
    <div
      className={cn(
        "flex items-center justify-between px-4 py-3 h-[52px] md:px-6 md:py-4 md:h-[70px] bg-white fixed top-0 left-0 w-full z-[100]",
        showHeader ? "" : "hidden",
      )}
    >
      <div className="absolute left-1/2 -translate-x-1/2">
        <img
          src="https://rsup2.b-cdn.net/medrite/Medrite_LogoBlue.png"
          alt={logoAlt}
          className="h-8 w-auto"
        />
      </div>

      {currentStateName === "storefrontBrowse" && (
        <>
          {(!hasInsurance || pendingPricesMode) &&
            (isMobile ? (
              <motion.button
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={handleAddInsurance}
                transition={{
                  duration: 0.1,
                  ease: "easeOut",
                }}
                className="absolute right-4"
              >
                <img
                  src="/images/kiosk/shieldIcon.svg"
                  alt="Shield"
                  className="w-9 h-9"
                />
              </motion.button>
            ) : (
              <button
                type="button"
                onClick={handleAddInsurance}
                className="absolute right-4"
              >
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.1,
                    ease: "easeOut",
                  }}
                  className="flex w-full items-center gap-1 pl-3 pr-4 py-2 rounded-lg border border-[#DBDBDB] bg-transparent text-black text-xs  transition-colors cursor-pointer"
                >
                  <Dot size={16} className="p-0" /> {t("ConnectYourInsurance")}
                </motion.div>
              </button>
            ))}
          {hasInsurance && !pendingPricesMode && (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger
                disabled={pricingStatus !== "SUCCESS"}
                className="absolute right-4"
              >
                {!isMobile ? (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.1,
                      ease: "easeOut",
                    }}
                    className="flex w-full items-center gap-1 pl-1 pr-4 py-2 rounded-[15px] border border-[#DBDBDB] text-xs"
                  >
                    <Dot
                      color="#00DD04"
                      size={32}
                      className="p-0"
                      data-testid="insurance-status-success"
                    />{" "}
                    {t("ConnectedToInsurance")}
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.1,
                      ease: "easeOut",
                    }}
                    className="relative inline-block"
                  >
                    <img
                      src="/images/kiosk/shieldIcon.svg"
                      alt="Shield"
                      className="w-9 h-9"
                    />
                    <Dot
                      color="#00DD04"
                      size={48}
                      className="absolute top-1 right-1 z-10 translate-x-1/2 -translate-y-1/2"
                      data-testid="insurance-status-success"
                    />
                  </motion.div>
                )}
              </PopoverTrigger>
              <PopoverContent
                align="end"
                sideOffset={8}
                className="flex flex-col justify-between items-start md:w-[393px] sm:w-[calc(100vw-48px)] md:mx-0 sm:mx-6 lg:mx-0 lg:p-[32px_24px_32px_24px] sm:p-6 flex-shrink-0 gap-8 rounded-3xl bg-white shadow-xl"
              >
                <div className="w-full flex flex-row justify-between">
                  <div className="flex flex-col gap-3">
                    <p className="font-suisse font-semibold md:text-2xl">
                      Hi, {firstName} {lastName?.slice(0, 1)}.
                    </p>

                    {/* Icons */}
                    <div className="relative w-fit">
                      <AccountModalSuperscriptIcon
                        className={"z-10 absolute"}
                      />
                      <div className="bg-white border-superscriptBlue border w-[44px] h-[44px] flex rounded-full items-center justify-center p-[2px] cursor-pointer absolute top-0 left-[28px] z-0">
                        <HeartHandshakeIcon color={"#00A3FF"} size={16} />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 min-w-[120px]">
                    <div className="flex flex-col">
                      <p
                        className="font-semibold font-mono text-[#939393] text-ellipsis
"
                      >
                        PRIMARY
                      </p>
                      <p>{primaryInsurance?.payer}</p>
                    </div>

                    <div className="flex flex-col">
                      <p className="font-mono text-[#939393]">ID#</p>
                      <p>{primaryInsurance?.memberId}</p>
                    </div>
                  </div>
                </div>

                {secondaryInsurance && (
                  <div className="w-full flex flex-row justify-between border-dashed border-t border-b border-[#939393] py-2">
                    <div className="flex flex-row gap-4">
                      <p className="font-semibold font-mono text-[#939393] text-sm">
                        SECONDARY
                      </p>
                      <p
                        className="font-semibold text-sm text-ellipsis
"
                      >
                        {secondaryInsurance?.payer}
                      </p>

                      <p className="font-mono text-[#939393] text-sm">ID#</p>
                      <p className="font-semibold text-sm">
                        {secondaryInsurance?.memberId}
                      </p>
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          )}
        </>
      )}
    </div>
  );
}
